import {
  PageContentContainer,
  PageLoading,
} from "@fp/ui/components/base/page-layout/page-layout";
import { Button } from "@fp/ui/components/base/buttons/button";
import { AdminLayout } from "@/components/admin-layout";
import { AdminPageHeader } from "@/components/admin-page-header";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useCallback, useEffect } from "react";
import { useDataRefresh } from "@/hooks/useSignalR";
import { toast } from "@/lib/toast";
import axios, { getErrorMessage } from "@/lib/axios";
import { AlertCircle, Clock } from "@untitledui/icons";
import { OnboardingProgressBar, type Milestone } from "@/components/onboarding-progress-bar";
import { useGetApiOnboardingProgress, getGetApiOnboardingProgressQueryKey } from "@fp/admin-api-client/onboarding-progress/onboarding-progress.client";
import { useGetSettings } from "@fp/admin-api-client/settings/settings.client";
import { useGetConfig } from "@fp/admin-api-client/config/config.client";
import { useFundingStatus } from "@/hooks/useFundingStatus";
import { useOrganizationTimeZone } from "@/hooks/useOrganizationTimeZone";
import { getGetCashflowQueryKey, getListScheduledDepositsQueryKey, getListShortfallsQueryKey, getListSkippedDepositsQueryKey, useCreateDepositPlan } from "@fp/admin-api-client/funding/funding.client";
import { FundAccountForm } from "@/components/funding/fund-account-form";
import { FundingAutomations } from "@/components/funding/funding-automations";
import { FundingStrategy } from "@/components/funding/funding-strategy";
import { AddFundsModal } from "@/components/funding/add-funds-modal";
import { CashflowCalendar } from "@/components/funding/cashflow-calendar";
import { Shortfalls } from "@/components/funding/shortfalls";
import { ScheduledDeposits } from "@/components/funding/scheduled-deposits";
import { BankAccounts } from "@/components/funding/bank-accounts";
import { Receivables } from "@/components/funding/receivables";
import { PayerOrganizations } from "@/components/funding/payer-organizations";
import { StateSelect } from "@fp/ui/components/base/select/state-select";
import { AddressFieldGroup } from "@fp/ui/components/common/address-field-group";
import type { OrganizationSettingsDto, BankAccountResponse, OrganizationFundingSettingsDto, InitiateBusinessVerificationRequest, BeneficialOwnerRequest, FundingTransactionDto, CashflowShortfallResponse } from "@fp/admin-api-client";
import { BusinessType, DataRefreshEvent } from "@fp/admin-api-client";
import { FundingTransactionRow } from "@/components/funding/funding-transaction-row";
import { toOrganizationDay } from "@/lib/organization-date";

interface AddFundsDefaults {
  amount: number;
  initiationDate: string;
}

const businessTypeLabels: Record<BusinessType, string> = {
  [BusinessType.SoleProprietorship]: "Sole Proprietorship",
  [BusinessType.Corporation]: "Corporation",
  [BusinessType.Llc]: "LLC",
  [BusinessType.Nonprofit]: "Nonprofit",
  [BusinessType.Partnership]: "Partnership",
};

const businessTypeOptions = Object.values(BusinessType).map((value) => ({
  value,
  label: businessTypeLabels[value],
}));

export const Route = createFileRoute("/funding/")({
  component: RouteComponent,
  pendingComponent: () => <PageLoading />,
});

const formatEin = (value: string): string => {
  const digits = value.replace(/\D/g, "").slice(0, 9);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}-${digits.slice(2)}`;
};

const formatSsn = (value: string): string => {
  const digits = value.replace(/\D/g, "").slice(0, 9);
  if (digits.length <= 3) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
};

const stripDashes = (value: string): string => value.replace(/-/g, "");

const emptyBeneficialOwner: BeneficialOwnerRequest = {
  email: "",
  firstName: "",
  lastName: "",
  dateOfBirth: "",
  ssn: "",
  address1: "",
  city: "",
  state: "",
  postalCode: "",
};

function AccountActivity() {
  const navigate = useNavigate();
  const { data: transactions } = useQuery({
    queryKey: ["fundingTransactions"],
    queryFn: async () => {
      const response = await axios.get<FundingTransactionDto[]>("/api/admin/funding/transactions");
      return response.data;
    },
  });

  const recent = (transactions ?? []).slice(0, 5);

  return (
    <section>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-gray-900">Account Activity</h3>
        {recent.length > 0 && (
          <Button size="sm" color="secondary" onClick={() => navigate({ to: "/funding/transactions" })}>
            View All
          </Button>
        )}
      </div>
      {recent.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-lg px-4 py-6 text-center text-sm text-gray-500">No activity yet.</div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-200">
          {recent.map((transaction) => (
            <FundingTransactionRow key={transaction.id} transaction={transaction} />
          ))}
        </div>
      )}
    </section>
  );
}

function RouteComponent() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const [addFundsDefaults, setAddFundsDefaults] = useState<AddFundsDefaults | null>(null);
  const [showVerificationForm, setShowVerificationForm] = useState(false);
  const [controllerIsOwner, setControllerIsOwner] = useState(false);
  const [verificationForm, setVerificationForm] = useState<InitiateBusinessVerificationRequest>({
    phone: "",
    businessName: "",
    businessType: BusinessType.Llc,
    address1: "",
    city: "",
    state: "",
    postalCode: "",
    adminFirstName: "",
    adminLastName: "",
    adminEmail: "",
    beneficialOwners: [],
  });

  const addBeneficialOwner = () => {
    setVerificationForm((f) => ({ ...f, beneficialOwners: [...(f.beneficialOwners ?? []), { ...emptyBeneficialOwner }] }));
  };

  const removeBeneficialOwner = (index: number) => {
    setVerificationForm((f) => ({ ...f, beneficialOwners: (f.beneficialOwners ?? []).filter((_, i) => i !== index) }));
    if (index === 0 && controllerIsOwner) setControllerIsOwner(false);
  };

  const updateBeneficialOwner = (index: number, patch: Partial<BeneficialOwnerRequest>) => {
    setVerificationForm((f) => ({
      ...f,
      beneficialOwners: (f.beneficialOwners ?? []).map((o, i) => (i === index ? { ...o, ...patch } : o)),
    }));
  };

  const toggleControllerAsOwner = (checked: boolean) => {
    if (checked) {
      const controllerOwner: BeneficialOwnerRequest = {
        email: verificationForm.controllerEmail ?? "",
        firstName: verificationForm.controllerFirstName ?? "",
        lastName: verificationForm.controllerLastName ?? "",
        dateOfBirth: verificationForm.controllerDateOfBirth ?? "",
        ssn: verificationForm.controllerSsn ? verificationForm.controllerSsn.replace(/\D/g, "").slice(-4) : "",
        address1: verificationForm.controllerAddress1 ?? "",
        address2: verificationForm.controllerAddress2,
        city: verificationForm.controllerCity ?? "",
        state: verificationForm.controllerState ?? "",
        postalCode: verificationForm.controllerPostalCode ?? "",
      };
      setVerificationForm((f) => ({ ...f, beneficialOwners: [controllerOwner, ...(f.beneficialOwners ?? [])] }));
      setControllerIsOwner(true);
    } else {
      setVerificationForm((f) => ({ ...f, beneficialOwners: (f.beneficialOwners ?? []).slice(1) }));
      setControllerIsOwner(false);
    }
  };

  const { data: fundingStatus, isLoading: isLoadingStatus } = useFundingStatus();
  const timeZoneId = useOrganizationTimeZone();
  const { data: onboardingProgressResponse } = useGetApiOnboardingProgress();
  const { data: userSettingsResponse } = useGetSettings();
  const { data: configResponse } = useGetConfig();
  const onboardingDismissed = userSettingsResponse?.data?.onboardingProgressState === "Dismissed";

  const invalidateOnboardingProgress = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: getGetApiOnboardingProgressQueryKey() });
  }, [queryClient]);

  const invalidateFundingAccounts = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["fundingAccounts"] });
    queryClient.invalidateQueries({ queryKey: ["fundingStatus"] });
  }, [queryClient]);

  const invalidateCashflow = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: getGetCashflowQueryKey() });
    queryClient.invalidateQueries({ queryKey: getListShortfallsQueryKey() });
  }, [queryClient]);

  const handleKybApproved = useCallback(() => {
    setTimeout(() => {
      invalidateOnboardingProgress();
      queryClient.invalidateQueries({ queryKey: ["fundingStatus"] });
    }, 2000);
  }, [invalidateOnboardingProgress, queryClient]);
  const invalidateDeposits = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: getListScheduledDepositsQueryKey() });
    queryClient.invalidateQueries({ queryKey: getListSkippedDepositsQueryKey() });
    queryClient.invalidateQueries({ queryKey: ["fundingStatus"] });
    queryClient.invalidateQueries({ queryKey: ["fundingTransactions"] });
    invalidateCashflow();
  }, [queryClient, invalidateCashflow]);

  useDataRefresh(DataRefreshEvent.OrganizationKybApproved, handleKybApproved);
  useDataRefresh(DataRefreshEvent.OrganizationAuthorizedByPaymentProvider, invalidateOnboardingProgress);
  useDataRefresh(DataRefreshEvent.OrganizationBankAccountLinked, () => { invalidateOnboardingProgress(); invalidateFundingAccounts(); });
  useDataRefresh(DataRefreshEvent.OrganizationBankAccountRemoved, () => { invalidateOnboardingProgress(); invalidateFundingAccounts(); });
  useDataRefresh(DataRefreshEvent.OrganizationFundingCompleted, () => { invalidateOnboardingProgress(); invalidateFundingAccounts(); invalidateDeposits(); });
  useDataRefresh(DataRefreshEvent.OrganizationFundingFailed, () => { invalidateFundingAccounts(); invalidateDeposits(); });
  useDataRefresh(DataRefreshEvent.OrganizationDepositInitiated, invalidateDeposits);
  useDataRefresh(DataRefreshEvent.OrganizationDepositAborted, invalidateDeposits);
  useDataRefresh(DataRefreshEvent.DepositsPlanned, invalidateDeposits);
  useDataRefresh(DataRefreshEvent.OrganizationPaymentDestinationDesignated, invalidateFundingAccounts);
  const onboardingProgress = onboardingProgressResponse?.data;

  const { data: accounts, isLoading: isLoadingAccounts } = useQuery({
    queryKey: ["fundingAccounts"],
    queryFn: async () => {
      const response = await axios.get<BankAccountResponse[]>("/api/admin/funding/accounts");
      return response.data;
    },
  });

  useEffect(() => {
    if (!accounts?.length || selectedAccountId) return;
    const fundingSourceAccount = accounts.find((a) => a.isFundingSource);
    setSelectedAccountId(fundingSourceAccount?.id ?? accounts[0].id);
  }, [accounts, selectedAccountId]);

  const { data: orgSettings } = useQuery({
    queryKey: ["organizationSettings"],
    queryFn: async () => {
      const response = await axios.get<OrganizationSettingsDto>("/api/organizations/current/settings");
      return response.data;
    },
  });

  const settingsMutation = useMutation({
    mutationFn: async (settings: { funding?: { autoTopUp?: Partial<OrganizationFundingSettingsDto["autoTopUp"]>; leadTimeBusinessDays?: number } }) => {
      if (!orgSettings) return;
      const { autoTopUp, ...funding } = settings.funding ?? {};
      const merged: OrganizationSettingsDto = {
        ...orgSettings,
        funding: {
          ...orgSettings.funding,
          ...funding,
          autoTopUp: { ...orgSettings.funding.autoTopUp, ...(autoTopUp ?? {}) },
        },
      };
      const response = await axios.put<OrganizationSettingsDto>("/api/organizations/current/settings", merged);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organizationSettings"] });
      queryClient.invalidateQueries({ queryKey: ["fundingStatus"] });
      invalidateCashflow();
    },
    onError: (err) => {
      toast.error(getErrorMessage(err, "Failed to save settings"));
    },
  });

  const initiateVerificationMutation = useMutation({
    mutationFn: async (formData: InitiateBusinessVerificationRequest) => {
      const response = await axios.post<{ verificationUrl: string }>(
        "/api/admin/funding/initiate-identity-verification",
        formData
      );
      return response.data;
    },
    onSuccess: (data) => {
      window.location.href = data.verificationUrl;
    },
    onError: (err) => {
      toast.error(getErrorMessage(err, "Failed to initiate verification"));
    },
  });

  const reauthMutation = useMutation({
    mutationFn: async () => {
      const response = await axios.get<{ verificationUrl: string }>(
        "/api/admin/funding/authorization-url"
      );
      return response.data;
    },
    onSuccess: (data) => {
      window.location.href = data.verificationUrl;
    },
    onError: (err) => {
      toast.error(getErrorMessage(err, "Failed to get authorization URL"));
    },
  });

  const createDepositPlanMutation = useCreateDepositPlan({
    mutation: {
      onSuccess: (response) => {
        queryClient.invalidateQueries({ queryKey: getListScheduledDepositsQueryKey() });
        queryClient.invalidateQueries({ queryKey: ["fundingStatus"] });
        queryClient.invalidateQueries({ queryKey: ["fundingTransactions"] });
        queryClient.invalidateQueries({ queryKey: ["dashboard"] });
        invalidateCashflow();
        setAddFundsDefaults(null);
        const depositCount = response.data.deposits.length;
        toast.success(depositCount > 1 ? `${depositCount} deposits planned` : "Deposit scheduled");
      },
      onError: (err) => {
        toast.error(getErrorMessage(err, "Failed to plan the deposit"));
      },
    },
  });

  const isNotSoleProp = verificationForm.businessType !== BusinessType.SoleProprietorship;

  const handleInitiateVerification = () => {
    const { phone, businessName, businessType, address1, city, state, postalCode, adminFirstName, adminLastName, adminEmail, adminCity, adminState } = verificationForm;
    if (!phone || !businessName || !businessType || !address1 || !city || !state || !postalCode || !adminFirstName || !adminLastName || !adminEmail || !adminCity || !adminState) {
      toast.error("Please fill in all required fields");
      return;
    }

    const phoneDigits = phone.replace(/\D/g, "");
    const isValidPhone = /^\+1\d{10}$/.test(phone) || phoneDigits.length === 10 || (phoneDigits.length === 11 && phoneDigits.startsWith("1"));
    if (!isValidPhone) {
      toast.error("Please enter a valid US phone number (e.g., 5125551234 or +15125551234)");
      return;
    }

    if (isNotSoleProp && !verificationForm.ein) {
      toast.error("EIN is required for non-sole proprietorship business types");
      return;
    }

    if (isNotSoleProp) {
      const { controllerFirstName, controllerLastName, controllerEmail, controllerTitle, controllerDateOfBirth, controllerSsn, controllerAddress1, controllerCity, controllerState, controllerPostalCode } = verificationForm;
      if (!controllerFirstName || !controllerLastName || !controllerEmail || !controllerTitle || !controllerDateOfBirth || !controllerSsn || !controllerAddress1 || !controllerCity || !controllerState || !controllerPostalCode) {
        toast.error("Please fill in all required Business Controller fields");
        return;
      }
    }

    const owners = verificationForm.beneficialOwners ?? [];

    const submissionData = {
      ...verificationForm,
      ein: verificationForm.ein ? stripDashes(verificationForm.ein) : undefined,
      controllerSsn: verificationForm.controllerSsn ? stripDashes(verificationForm.controllerSsn) : undefined,
      beneficialOwners: owners,
    };
    initiateVerificationMutation.mutate(submissionData);
  };

  const handleCreateDepositPlan = (amount: number, sourceAccountId: string, initiationDate: string) => {
    if (!sourceAccountId) {
      toast.error("Please select an account");
      return;
    }
    createDepositPlanMutation.mutate({ data: { sourceAccountId, amount, initiationDate } });
  };

  const today = timeZoneId ? toOrganizationDay(new Date(), timeZoneId) : "";
  const balance = fundingStatus?.balance ?? 0;
  const targetBalance = orgSettings?.funding.autoTopUp.targetBalance ?? 0;

  const openAddFunds = () => {
    setAddFundsDefaults({ amount: targetBalance > balance ? Math.ceil(targetBalance - balance) : 0, initiationDate: today });
  };

  const scheduleDepositFor = (shortfall: CashflowShortfallResponse) => {
    setAddFundsDefaults({ amount: Math.ceil(shortfall.amountShort), initiationDate: shortfall.depositDay });
  };

  const isLoading = isLoadingStatus || isLoadingAccounts;

  if (isLoading) {
    return (
      <AdminLayout isWide>
        <AdminPageHeader
          title="Funding"
          parent={{ url: "/dashboard", label: "Home" }}
        />
        <PageContentContainer>
          <PageLoading />
        </PageContentContainer>
      </AdminLayout>
    );
  }

  const verificationStatus = fundingStatus?.identityVerification?.status;
  const isVerificationNotStarted = verificationStatus === "NotStarted";
  const isVerificationPending = verificationStatus === "Pending";
  const isVerificationRejected = verificationStatus === "Rejected";
  const hasCompletedVerification = fundingStatus?.identityVerification?.hasCompleted ?? false;
  const isReadyForFunding = fundingStatus?.identityVerification?.isReadyForFunding ?? false;
  const canReauthorize = fundingStatus?.identityVerification?.canReauthorize ?? false;
  const requiresReverification = fundingStatus?.identityVerification?.requiresReverification ?? false;
  const isApprovedButNeedsCredential = hasCompletedVerification && !isReadyForFunding;
  const showVerificationPrompt = fundingStatus && isVerificationNotStarted;
  const showLinkAccountPrompt = isReadyForFunding && fundingStatus && !fundingStatus.hasLinkedAccount;
  const showFundingForm = isReadyForFunding && fundingStatus && fundingStatus.hasLinkedAccount;
  const showOnboardingBar = Boolean(onboardingProgress && !onboardingDismissed && !showFundingForm && !showVerificationForm && (showVerificationPrompt || showLinkAccountPrompt || isVerificationPending || isVerificationRejected || isApprovedButNeedsCredential));

  const fundingSteps: Milestone[] = [
    { key: "BusinessVerified", label: "Verify Your Business", description: "One-time verification that usually takes 24\u201348 hours, but could be sooner.", cta: "Start Verification", verb: "Start", onClick: () => setShowVerificationForm(true) },
    requiresReverification
      ? { key: "AstraAuthorized", label: "Re-verify Your Business", description: "Your business verification is incomplete, so your payment account can't be connected. Re-verify to restore funding.", cta: "Re-verify", verb: "Re-verify", onClick: () => setShowVerificationForm(true) }
      : { key: "AstraAuthorized", label: "Authorize Your Account", description: "Complete a quick authorization to connect your payment account.", cta: "Continue", verb: "Authorize", onClick: () => reauthMutation.mutate(), isLoading: reauthMutation.isPending },
    { key: "BankAccountLinked", label: "Link Bank Account", description: "Connect your bank account via Plaid to fund your FieldPay account.", cta: "Link Bank Account", verb: "Link", route: "/funding/link-account" },
    { key: "AccountFunded", label: "Fund Your Account", description: "Add money to your FieldPay account so you can pay your team instantly.", cta: "Fund Your Account", verb: "Fund", route: "/funding" },
  ];

  return (
    <AdminLayout isWide>
      <AdminPageHeader
        title="Funding"
        parent={{ url: "/dashboard", label: "Home" }}
      />
      <PageContentContainer>
        <div className="space-y-6">
          {showOnboardingBar && (
            <OnboardingProgressBar progress={onboardingProgress!} steps={fundingSteps} />
          )}

          {(showVerificationPrompt || isVerificationRejected || requiresReverification) && showVerificationForm && (
            <section>
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h2 className="text-xl font-semibold mb-6">Business Verification</h2>

                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-medium mb-4">Business Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Business Name *</label>
                        <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" value={verificationForm.businessName} onChange={(e) => setVerificationForm((f) => ({ ...f, businessName: e.target.value }))} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Business Type *</label>
                        <select className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" value={verificationForm.businessType} onChange={(e) => setVerificationForm((f) => ({ ...f, businessType: e.target.value as BusinessType }))}>
                          {businessTypeOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                        <input type="tel" className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" placeholder="+15125551234" value={verificationForm.phone} onChange={(e) => setVerificationForm((f) => ({ ...f, phone: e.target.value }))} />
                        <p className="text-xs text-gray-500 mt-1">You'll use this number to verify your identity on the next step.</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">EIN{isNotSoleProp ? " *" : ""}</label>
                        <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" placeholder="XX-XXXXXXX" value={verificationForm.ein ?? ""} onChange={(e) => setVerificationForm((f) => ({ ...f, ein: formatEin(e.target.value) || undefined }))} />
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-medium mb-1">Business Address</h3>
                    <p className="text-sm text-gray-500 mb-4">This is the address associated with your EIN</p>
                    <AddressFieldGroup fields={["addressLine1", "city", "state", "zip"]} value={{ addressLine1: verificationForm.address1, city: verificationForm.city, state: verificationForm.state, zip: verificationForm.postalCode }} onChange={(v) => setVerificationForm((f) => ({ ...f, address1: v.addressLine1 ?? "", city: v.city ?? "", state: v.state ?? "", postalCode: v.zip ?? "" }))} />
                  </div>

                  <div>
                    <h3 className="text-lg font-medium mb-1">Business Admin</h3>
                    <p className="text-sm text-gray-500 mb-4">This is the principal user of the account</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                        <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" value={verificationForm.adminFirstName} onChange={(e) => setVerificationForm((f) => ({ ...f, adminFirstName: e.target.value }))} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                        <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" value={verificationForm.adminLastName} onChange={(e) => setVerificationForm((f) => ({ ...f, adminLastName: e.target.value }))} />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                        <input type="email" className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" value={verificationForm.adminEmail} onChange={(e) => setVerificationForm((f) => ({ ...f, adminEmail: e.target.value }))} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
                        <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" value={verificationForm.adminCity ?? ""} onChange={(e) => setVerificationForm((f) => ({ ...f, adminCity: e.target.value || undefined }))} />
                      </div>
                      <div>
                        <StateSelect label="State *" selectClassName="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" value={verificationForm.adminState ?? ""} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setVerificationForm((f) => ({ ...f, adminState: e.target.value || undefined }))} />
                      </div>
                    </div>
                  </div>

                  {isNotSoleProp && (
                    <div className="flex flex-col gap-4">
                      <div>
                        <h3 className="text-lg font-medium mb-1">Business Controller</h3>
                        <p className="text-sm text-gray-500">The individual with significant control over the management and direction of the business (e.g., CEO, CFO, or equivalent)</p>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                          <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" value={verificationForm.controllerFirstName ?? ""} onChange={(e) => setVerificationForm((f) => ({ ...f, controllerFirstName: e.target.value || undefined }))} />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                          <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" value={verificationForm.controllerLastName ?? ""} onChange={(e) => setVerificationForm((f) => ({ ...f, controllerLastName: e.target.value || undefined }))} />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                          <input type="email" className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" value={verificationForm.controllerEmail ?? ""} onChange={(e) => setVerificationForm((f) => ({ ...f, controllerEmail: e.target.value || undefined }))} />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                          <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" placeholder="e.g., CFO" value={verificationForm.controllerTitle ?? ""} onChange={(e) => setVerificationForm((f) => ({ ...f, controllerTitle: e.target.value || undefined }))} />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth *</label>
                          <input type="date" className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" value={verificationForm.controllerDateOfBirth ?? ""} onChange={(e) => setVerificationForm((f) => ({ ...f, controllerDateOfBirth: e.target.value || undefined }))} />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">SSN *</label>
                          <input type="password" className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" placeholder="XXX-XX-XXXX" value={verificationForm.controllerSsn ?? ""} onChange={(e) => setVerificationForm((f) => ({ ...f, controllerSsn: formatSsn(e.target.value) || undefined }))} />
                        </div>
                      </div>
                      <AddressFieldGroup fields={["addressLine1", "city", "state", "zip"]} value={{ addressLine1: verificationForm.controllerAddress1, city: verificationForm.controllerCity, state: verificationForm.controllerState, zip: verificationForm.controllerPostalCode }} onChange={(v) => setVerificationForm((f) => ({ ...f, controllerAddress1: v.addressLine1 || undefined, controllerCity: v.city || undefined, controllerState: v.state || undefined, controllerPostalCode: v.zip || undefined }))} />
                    </div>
                  )}

                  {isNotSoleProp && (
                    <div className="flex flex-col gap-4">
                      <div>
                        <h3 className="text-lg font-medium mb-1">Beneficial Owners</h3>
                        <p className="text-sm text-gray-500">Include any individuals who exercise substantial ownership or control over the business and are not the controller. Optional.</p>
                      </div>

                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="controller-is-owner"
                          checked={controllerIsOwner}
                          onChange={(e) => toggleControllerAsOwner(e.target.checked)}
                        />
                        <label htmlFor="controller-is-owner" className="text-sm text-gray-700">
                          Controller is also a beneficial owner (pre-fill first row with controller data)
                        </label>
                      </div>

                      {(verificationForm.beneficialOwners ?? []).map((owner, i) => (
                        <div key={i} className="border border-gray-200 rounded-md p-4 flex flex-col gap-4">
                          <div className="flex items-center justify-between">
                            <h4 className="text-sm font-semibold text-gray-700">Beneficial Owner {i + 1}</h4>
                            <button type="button" className="text-xs text-red-600 hover:text-red-700" onClick={() => removeBeneficialOwner(i)}>
                              Remove
                            </button>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                              <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" value={owner.firstName} onChange={(e) => updateBeneficialOwner(i, { firstName: e.target.value })} />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                              <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" value={owner.lastName} onChange={(e) => updateBeneficialOwner(i, { lastName: e.target.value })} />
                            </div>
                            <div className="md:col-span-2">
                              <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                              <input type="email" className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" value={owner.email} onChange={(e) => updateBeneficialOwner(i, { email: e.target.value })} />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth *</label>
                              <input type="date" className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" value={owner.dateOfBirth} onChange={(e) => updateBeneficialOwner(i, { dateOfBirth: e.target.value })} />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">SSN (last 4) *</label>
                              <input type="password" inputMode="numeric" maxLength={4} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" placeholder="XXXX" value={owner.ssn} onChange={(e) => updateBeneficialOwner(i, { ssn: e.target.value.replace(/\D/g, "").slice(0, 4) })} />
                            </div>
                          </div>
                          <AddressFieldGroup
                            fields={["addressLine1", "city", "state", "zip"]}
                            value={{ addressLine1: owner.address1, city: owner.city, state: owner.state, zip: owner.postalCode }}
                            onChange={(v) => updateBeneficialOwner(i, { address1: v.addressLine1 ?? "", city: v.city ?? "", state: v.state ?? "", postalCode: v.zip ?? "" })}
                          />
                        </div>
                      ))}

                      <Button color="secondary" onClick={addBeneficialOwner}>
                        Add Beneficial Owner
                      </Button>
                    </div>
                  )}

                  <div className="flex gap-3">
                    <Button onClick={handleInitiateVerification} color="primary" isLoading={initiateVerificationMutation.isPending}>
                      Submit Verification
                    </Button>
                    <Button onClick={() => setShowVerificationForm(false)} color="secondary">
                      Cancel
                    </Button>
                  </div>
                </div>
              </div>
            </section>
          )}

          {showVerificationPrompt && !showVerificationForm && !showOnboardingBar && (
            <section>
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h2 className="text-xl font-semibold mb-2">Verify Your Business</h2>
                <p className="text-gray-600 mb-4">
                  Verify your business to enable funding and payments. This one-time check usually takes 24–48 hours, but could be sooner.
                </p>
                <Button onClick={() => setShowVerificationForm(true)} color="primary">
                  Start Verification
                </Button>
              </div>
            </section>
          )}

          {isVerificationPending && !showVerificationForm && (
            <section>
              <div className="bg-blue-50 border border-blue-300 rounded-lg p-6">
                <div className="flex items-start gap-3">
                  <Clock className="text-blue-600 size-6 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <h2 className="text-xl font-semibold text-blue-700 mb-2">Business Verification In Review</h2>
                    <p className="text-blue-600">
                      Your business details have been submitted to our payments partner. Verification typically completes within 1–2 business days. You'll be notified by email once approved — bank linking and funding become available at that point.
                    </p>
                  </div>
                </div>
              </div>
            </section>
          )}

          {isVerificationRejected && !showVerificationForm && (
            <section>
              <div className="bg-red-50 border border-red-300 rounded-lg p-6">
                <div className="flex items-start gap-3">
                  <AlertCircle className="text-red-600 size-6 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <h2 className="text-xl font-semibold text-red-700 mb-2">Verification Unsuccessful</h2>
                    <p className="text-red-600 mb-4">
                      Your business verification was not approved. Please contact support for assistance or try again.
                    </p>
                    <Button
                      onClick={() => setShowVerificationForm(true)}
                      color="primary"
                    >
                      Retry Verification
                    </Button>
                  </div>
                </div>
              </div>
            </section>
          )}

          {canReauthorize && (
            <section>
              <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-6">
                <div className="flex items-start gap-3">
                  <AlertCircle className="text-yellow-600 size-6 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <h2 className="text-xl font-semibold text-yellow-700 mb-2">Re-authorization Required</h2>
                    <p className="text-yellow-600 mb-4">
                      Your Astra connection needs to be re-authorized. This can happen if your session expired or was revoked. Click below to complete a quick re-authorization.
                    </p>
                    <Button
                      onClick={() => reauthMutation.mutate()}
                      color="primary"
                      isLoading={reauthMutation.isPending}
                    >
                      Re-authorize
                    </Button>
                  </div>
                </div>
              </div>
            </section>
          )}

          {requiresReverification && !showVerificationForm && (
            <section>
              <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-6">
                <div className="flex items-start gap-3">
                  <AlertCircle className="text-yellow-600 size-6 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <h2 className="text-xl font-semibold text-yellow-700 mb-2">Re-verification Required</h2>
                    <p className="text-yellow-600 mb-4">
                      Your business verification is incomplete, so your payment account can't be connected. Re-verify your business to restore funding.
                    </p>
                    <Button onClick={() => setShowVerificationForm(true)} color="primary">
                      Re-verify Your Business
                    </Button>
                  </div>
                </div>
              </div>
            </section>
          )}

          {showLinkAccountPrompt && !showOnboardingBar && (
            <section>
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h2 className="text-xl font-semibold mb-2">Link a Bank Account</h2>
                <p className="text-gray-600 mb-4">
                  Your business is verified and your payment account is connected. Link a bank account to start funding your FieldPay balance.
                </p>
                <Button onClick={() => navigate({ to: "/funding/link-account" })} color="primary">
                  Link Bank Account
                </Button>
              </div>
            </section>
          )}

          {(showFundingForm || (hasCompletedVerification && fundingStatus?.hasLinkedAccount)) && (
            <>
              <div className="grid gap-6 lg:grid-cols-2 lg:items-start">
                <FundAccountForm
                  balance={fundingStatus!.balance}
                  lowBalanceThreshold={fundingStatus!.lowBalanceThreshold}
                  lowBalanceWarningWarranted={fundingStatus!.lowBalanceWarningWarranted}
                  onAddFunds={isReadyForFunding ? openAddFunds : undefined}
                />

                <Shortfalls
                  canSchedule={isReadyForFunding}
                  onScheduleDeposit={scheduleDepositFor}
                  leadTime={orgSettings && configResponse ? {
                    leadTimeBusinessDays: orgSettings.funding.leadTimeBusinessDays,
                    clearingWindowBusinessDays: configResponse.data.funding.clearingWindowBusinessDays,
                    onSave: (leadTimeBusinessDays) => settingsMutation.mutate({ funding: { leadTimeBusinessDays } }),
                    isSaving: settingsMutation.isPending,
                  } : undefined}
                />
              </div>

              {timeZoneId && orgSettings && (
                <CashflowCalendar
                  timeZoneId={timeZoneId}
                  targetBalance={orgSettings.funding.autoTopUp.enabled ? orgSettings.funding.autoTopUp.targetBalance : 0}
                />
              )}

              <div className="grid gap-6 lg:grid-cols-2 lg:items-start">
                <ScheduledDeposits canSchedule={isReadyForFunding} />

                {isReadyForFunding && (
                  <div className="space-y-6">
                    <FundingStrategy canSchedule={isReadyForFunding} payoutPolicyEnabled={orgSettings?.payoutPolicyEnabled ?? false} autoTopUpEnabled={orgSettings?.funding.autoTopUp.enabled ?? false} />

                    <FundingAutomations
                      autoTopUpEnabled={orgSettings?.funding.autoTopUp.enabled ?? false}
                      autoTopUpThreshold={orgSettings?.funding.autoTopUp.threshold ?? 0}
                      targetBalance={orgSettings?.funding.autoTopUp.targetBalance ?? 0}
                      fundingSourceAccount={accounts?.find((a) => a.isFundingSource) ?? null}
                      onSave={(settings) => settingsMutation.mutate(settings)}
                      isSaving={settingsMutation.isPending}
                    />
                  </div>
                )}
              </div>

              <div className="grid gap-6 lg:grid-cols-2 lg:items-start">
                <div className="space-y-6">
                  <AccountActivity />

                  <Receivables />
                </div>

                <div className="space-y-6">
                  <BankAccounts accounts={accounts ?? []} />

                  <PayerOrganizations />
                </div>
              </div>
            </>
          )}
        </div>
      </PageContentContainer>

      <AddFundsModal
        isOpen={addFundsDefaults !== null}
        onClose={() => setAddFundsDefaults(null)}
        balance={balance}
        defaultAmount={addFundsDefaults?.amount ?? 0}
        defaultInitiationDate={addFundsDefaults?.initiationDate ?? today}
        accounts={accounts || []}
        selectedAccountId={selectedAccountId}
        timeZoneId={timeZoneId}
        onSubmit={handleCreateDepositPlan}
        isLoading={createDepositPlanMutation.isPending}
      />
    </AdminLayout>
  );
}